import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Sparkles } from 'lucide-react';
export function Mechanism() {
  return <section className="bg-white py-24 relative overflow-hidden" data-id="element-60">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-brand-yellow/5 -skew-x-12 translate-x-20 pointer-events-none" data-id="element-61" />
      <div className="absolute top-20 left-10 w-32 h-32 bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" data-id="element-62" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-63">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-20" data-id="element-64">
          {/* Industry Secret pill */}
          <div className="inline-flex items-center gap-2 bg-brand-dark text-brand-yellow font-bold px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.25em] mb-10 shadow-xl" data-id="element-65">
            <Sparkles className="w-4 h-4" strokeWidth={3} data-id="element-66" />
            <span data-id="element-67">Industry Secret Unlocked</span>
          </div>

          {/* "Discover" with decorative lines */}
          <div className="flex items-center justify-center gap-4 mb-8" data-id="element-68">
            <span className="h-px w-12 sm:w-20 bg-gradient-to-r from-transparent to-gray-300" data-id="element-69" />
            <p className="text-xs sm:text-sm font-bold text-gray-600 uppercase tracking-[0.5em]" data-id="element-70">
              Discover
            </p>
            <span className="h-px w-12 sm:w-20 bg-gradient-to-l from-transparent to-gray-300" data-id="element-71" />
          </div>

          {/* Main title */}
          <h2 className="relative inline-block text-balance" data-id="element-72">
            {/* TM badge */}
            <span className="absolute -top-3 -right-2 sm:-right-6 z-30 inline-flex items-center justify-center bg-brand-dark text-brand-yellow text-[10px] sm:text-xs font-bold px-2 py-1 rounded-md rotate-6 shadow-lg font-body tracking-widest" data-id="element-73">
              TM
            </span>

            <span className="block text-3xl sm:text-5xl lg:text-6xl text-brand-dark/80 font-display tracking-wider mb-3 sm:mb-5" data-id="element-74">
              The
            </span>

            <span className="block text-5xl sm:text-7xl lg:text-8xl leading-[0.95] font-display tracking-tight" data-id="element-75">
              <span className="relative inline-block" data-id="element-76">
                <span className="absolute inset-x-0 bottom-2 sm:bottom-3 h-4 sm:h-6 bg-brand-yellow -skew-y-1" data-id="element-77" />
                <span className="relative text-brand-dark italic" data-id="element-78">
                  "Frictionless
                </span>
              </span>
            </span>

            <span className="block text-5xl sm:text-7xl lg:text-8xl leading-[0.95] font-display tracking-tight mt-2" data-id="element-79">
              <span className="relative inline-block" data-id="element-80">
                <span className="absolute inset-x-0 bottom-2 sm:bottom-3 h-4 sm:h-6 bg-brand-yellow -skew-y-1" data-id="element-81" />
                <span className="relative text-brand-dark italic" data-id="element-82">
                  Turnover Cleaning
                </span>
              </span>
            </span>

            <span className="block text-5xl sm:text-7xl lg:text-8xl leading-[0.95] font-display tracking-tight mt-2" data-id="element-83">
              <span className="relative inline-block" data-id="element-84">
                <span className="absolute inset-x-0 bottom-2 sm:bottom-3 h-4 sm:h-6 bg-brand-yellow -skew-y-1" data-id="element-85" />
                <span className="relative text-brand-dark italic" data-id="element-86">System"</span>
              </span>
            </span>
          </h2>

          {/* Copyright line */}
          <div className="flex items-center justify-center gap-3 mt-10" data-id="element-87">
            <span className="h-1 w-1 rounded-full bg-gray-400" data-id="element-88" />
            <p className="text-xs sm:text-sm text-gray-500 italic font-medium tracking-wide" data-id="element-89">
              © Copyrighted Methodology — Available Exclusively Inside The
              Protocol
            </p>
            <span className="h-1 w-1 rounded-full bg-gray-400" data-id="element-90" />
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="bg-gray-50 border border-gray-200 rounded-2xl p-8 sm:p-12 shadow-lg" data-id="element-91">
          <div className="flex items-center gap-3 mb-6" data-id="element-92">
            <Zap className="w-7 h-7 text-yellow-600 flex-shrink-0" strokeWidth={2.5} data-id="element-93" />
            <p className="text-2xl font-bold text-brand-dark m-0" data-id="element-94">
              This isn't a cleaning guide—it's a tactical movement protocol.
            </p>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-8" data-id="element-95">
            It uses a{' '}
            <span className="inline-block bg-brand-dark text-brand-yellow font-bold px-3 py-1 rounded-full text-sm mx-1 uppercase tracking-wide" data-id="element-96">
              Top-Down, Back-Out
            </span>{' '}
            pathing model that ensures you only touch each surface once.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-97">
            By focusing on{' '}
            <span className="inline-block bg-brand-dark text-brand-yellow font-bold px-3 py-1 rounded-full text-sm mx-1 uppercase tracking-wide" data-id="element-98">
              Visual Anchors
            </span>{' '}
            (the 5 specific spots guests check to judge the whole house), you
            create the psychological effect of a deep clean in 25% of the time.
          </p>
        </motion.div>
      </div>
    </section>;
}